import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api, fetchAll, request } from "./client";
import type { ListPage } from "./client";
import type { FiscalMode } from "@fiskhub/shared";
import type {
  AuditEntry,
  Certificate,
  Company,
  Device,
  FiscalHealth,
  Health,
  Invoice,
  InvoiceDetail,
  InvoiceResponse,
  IsuImportResult,
  IsuImportRow,
  Overview,
  Session,
  User,
} from "./types";
import type { InvitationPreview, InvitedUser } from "./types";
import type { ApiKey, CreatedApiKey } from "./types";

/** One place for every cache key, so invalidation is never guesswork. */
export const keys = {
  session: ["session"] as const,
  overview: ["overview"] as const,
  health: ["health"] as const,
  fiscalHealth: ["fiscal-health"] as const,
  /**
   * The cross-tenant scope is part of the key, never a mutation of the same
   * one: a tenant-scoped list and an all-tenants list are different data and
   * must never be served to each other out of the cache.
   */
  companies: (allTenants = false) =>
    ["companies", "list", allTenants ? "all-tenants" : "tenant"] as const,
  company: (id: string) => ["companies", id] as const,
  certificates: (companyId?: string) => ["certificates", companyId ?? "all"] as const,
  devices: (companyId?: string, allTenants = false) =>
    ["devices", "list", companyId ?? "all", allTenants ? "all-tenants" : "tenant"] as const,
  invoices: (filters: unknown) => ["invoices", filters] as const,
  apiKeys: (includeRevoked = false) =>
    ["api-keys", includeRevoked ? "with-revoked" : "active"] as const,
  invoice: (id: string) => ["invoices", id] as const,
  users: ["users"] as const,
  audit: (filters: unknown) => ["audit", filters] as const,
};

// Item endpoints return a bare object. LIST endpoints return the envelope
// published in `openapi/fiskhub.v1.yaml`:
//
//   { object: "list", data: [...], has_more, next_cursor }
//
// Screens that show a whole collection call `fetchAll`, which follows
// `next_cursor` and hands back a plain array — showing only the first page
// would silently hide rows. The two screens with their own paging UI (invoices,
// audit log) take the envelope as it comes, because they need `has_more`.
//
// Errors are wrapped too (`{ error: { … } }`); a bare object is only ever a
// single resource.

// -----------------------------------------------------------------------------
// Session
// -----------------------------------------------------------------------------

export function useSession(): UseQueryResult<Session, unknown> {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<Session>("/auth/session"),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface LoginInput {
  email: string;
  password: string;
  /**
   * Which tenant the email belongs to. Optional: the API falls back to the
   * request's subdomain, then to the sole active tenant, and only refuses when
   * neither resolves. Sent as `tenant_slug` because that is the field the
   * login endpoint declares.
   */
  tenant_slug?: string;
}

export function useLogin(): UseMutationResult<Session, unknown, LoginInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<Session>("/auth/login", input),
    onSuccess: (session) => {
      queryClient.setQueryData(keys.session, session);
      void queryClient.invalidateQueries();
    },
  });
}

export function useLogout(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// -----------------------------------------------------------------------------
// Dashboard home
// -----------------------------------------------------------------------------

export function useOverview(): UseQueryResult<Overview, unknown> {
  return useQuery({
    queryKey: keys.overview,
    queryFn: () => api.get<Overview>("/metrics/overview"),
    // Deadline and certificate numbers come from Postgres, not from log lines
    // (§13.5), so they are cheap enough to refresh on a timer.
    refetchInterval: 60_000,
  });
}

/**
 * Which API build is answering.
 *
 * Long `staleTime` and no polling: a build identity changes only when the
 * container is replaced, and a replaced container means a fresh page load
 * anyway. `retry: false` so a down API shows as unknown immediately rather than
 * after three backed-off attempts — the sidebar is not the place to discover an
 * outage slowly.
 */
export function useHealth(): UseQueryResult<Health, unknown> {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => api.get<Health>("/health"),
    staleTime: 5 * 60_000,
    retry: false,
    // Mounted once, in the app shell, and its answer only changes when the
    // container is replaced — which is a fresh page load anyway. Exempt from
    // the app-wide refetch-on-mount so it is not re-asked on every navigation.
    refetchOnMount: false,
  });
}

export function useFiscalHealth(): UseQueryResult<FiscalHealth, unknown> {
  return useQuery({
    queryKey: keys.fiscalHealth,
    queryFn: () => api.get<FiscalHealth>("/health/fiscal"),
    refetchInterval: 60_000,
    retry: false,
  });
}

// -----------------------------------------------------------------------------
// Companies
// -----------------------------------------------------------------------------

export interface CrossTenantOption {
  /**
   * Ask for every tenant's rows (`?all_tenants=true`). Honoured server-side
   * only for a session principal carrying `users.is_platform_admin`; anyone
   * else gets their own tenant, so this is a request, never an assumption.
   */
  allTenants?: boolean;
}

export function useCompanies(options: CrossTenantOption = {}): UseQueryResult<Company[], unknown> {
  const allTenants = options.allTenants === true;
  return useQuery({
    queryKey: keys.companies(allTenants),
    queryFn: async () =>
      fetchAll<Company>("/companies", allTenants ? { all_tenants: true } : {}),
    staleTime: 60_000,
  });
}

export function useCompany(id: string): UseQueryResult<Company, unknown> {
  return useQuery({
    queryKey: keys.company(id),
    queryFn: () => api.get<Company>(`/companies/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateCompany(): UseMutationResult<Company, unknown, Partial<Company>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Company>) => api.post<Company>("/companies", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useUpdateCompany(): UseMutationResult<
  Company,
  unknown,
  { id: string; patch: Partial<Company> }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Company> }) =>
      api.patch<Company>(`/companies/${id}`, patch),
    onSuccess: (company) => {
      queryClient.setQueryData(keys.company(company.id), company);
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

// -----------------------------------------------------------------------------
// Certificates
// -----------------------------------------------------------------------------

export function useCertificates(companyId?: string): UseQueryResult<Certificate[], unknown> {
  return useQuery({
    queryKey: keys.certificates(companyId),
    queryFn: async () =>
      fetchAll<Certificate>("/certificates", companyId ? { company_id: companyId } : {}),
  });
}

export interface CertificateUploadInput {
  company_id: string;
  /** Optional. The API defaults it to the uploaded file's name. */
  label?: string;
  passphrase: string;
  file: File;
}

export function useUploadCertificate(): UseMutationResult<
  Certificate,
  unknown,
  CertificateUploadInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CertificateUploadInput) => {
      // Field names are the published contract's, exactly:
      // `openapi/fiskhub.v1.yaml` -> POST /certificates ->
      // required: [company_id, file, passphrase].
      //
      // They were `certificate` and `password` here, which multer rejected with
      // "Unexpected file field" before any of it reached validation.
      const form = new FormData();
      form.set("company_id", input.company_id);
      // Used once to open the PKCS#12 and never persisted in plaintext (§8).
      form.set("passphrase", input.passphrase);
      form.set("file", input.file, input.file.name);
      // Omitted rather than sent empty: absent means "default to the file
      // name", whereas "" is a value and would have to be rejected.
      if (input.label) form.set("label", input.label);
      // `mode` is deliberately NOT sent. A certificate inherits its company's
      // mode (`services/certificates.ts`), and it is not in the contract — a
      // field the caller can set but the server ignores is worse than none.
      return api.upload<Certificate>("/certificates", form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["certificates"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

/**
 * Archive a certificate.
 *
 * `POST /certificates/{id}/archive`, not a status PATCH — there is no PATCH
 * route on `/certificates` and there is nothing else to change: the API models
 * this as one named action because archiving is the ONLY transition a
 * certificate has. It was written as `PATCH /certificates/{id}` here, which the
 * API answered with "No such endpoint", so the Archive button could never work.
 *
 * There is no delete, either here or upstream: the invoices a certificate
 * signed stay auditable against it (invariant 7).
 */
export function useArchiveCertificate(): UseMutationResult<Certificate, unknown, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post<Certificate>(`/certificates/${id}/archive`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["certificates"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

// -----------------------------------------------------------------------------
// Country schemas — drives the generic form renderer (§6)
// -----------------------------------------------------------------------------


// -----------------------------------------------------------------------------
// Devices
// -----------------------------------------------------------------------------

export function useDevices(
  companyId?: string,
  options: CrossTenantOption = {},
): UseQueryResult<Device[], unknown> {
  const allTenants = options.allTenants === true;
  return useQuery({
    queryKey: keys.devices(companyId, allTenants),
    queryFn: async () =>
      fetchAll<Device>("/devices", {
        ...(companyId ? { company_id: companyId } : {}),
        ...(allTenants ? { all_tenants: true } : {}),
      }),
  });
}

export function useCreateDevice(): UseMutationResult<Device, unknown, Partial<Device>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Device>) => api.post<Device>("/devices", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useUpdateDevice(): UseMutationResult<
  Device,
  unknown,
  { id: string; patch: Partial<Device> }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Device> }) =>
      api.patch<Device>(`/devices/${id}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useImportIsuNumbers(): UseMutationResult<IsuImportResult, unknown, IsuImportRow[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: IsuImportRow[]) => api.post<IsuImportResult>("/devices/import", { rows }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}


// -----------------------------------------------------------------------------
// API keys
// -----------------------------------------------------------------------------

/**
 * The device credential (`Authorization: Bearer fh_live_… / fh_test_…`).
 *
 * Session-only and `admin` and above, enforced server-side: a key must never be
 * able to mint another key, or one leaked device credential becomes
 * self-perpetuating and revoking the original achieves nothing.
 */
export function useApiKeys(includeRevoked = false): UseQueryResult<ApiKey[], unknown> {
  return useQuery({
    queryKey: keys.apiKeys(includeRevoked),
    queryFn: () =>
      fetchAll<ApiKey>("/api-keys", includeRevoked ? { include_revoked: "true" } : {}),
  });
}

export interface CreateApiKeyInput {
  name: string;
  mode: FiscalMode;
  company_id: string | null;
  scopes: string[];
}

/**
 * Mint a key. The response carries `secret` — the ONLY time it exists.
 *
 * Deliberately not written into the query cache: the secret would then sit in
 * memory for as long as the cache entry lives, and be handed to anything that
 * later reads the list. The screen holds it in component state and drops it
 * when the dialog closes.
 */
export function useCreateApiKey(): UseMutationResult<CreatedApiKey, unknown, CreateApiKeyInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiKeyInput) => api.post<CreatedApiKey>("/api-keys", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
}

export function useRevokeApiKey(): UseMutationResult<ApiKey, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ApiKey>(`/api-keys/${id}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
}

// -----------------------------------------------------------------------------
// Invoices
// -----------------------------------------------------------------------------

export interface InvoiceFilters {
  company_id?: string;
  device_id?: string;
  premises_code?: string;
  status?: string;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

/**
 * One page of invoices. Unlike the collection hooks above this keeps the
 * envelope: the screen has Previous/Next and needs `has_more` and
 * `next_cursor` to drive them.
 */
export function useInvoices(filters: InvoiceFilters): UseQueryResult<ListPage<Invoice>, unknown> {
  return useQuery({
    queryKey: keys.invoices(filters),
    queryFn: () => api.get<ListPage<Invoice>>("/invoices", { query: { ...filters } }),
    placeholderData: (previous) => previous,
  });
}

/**
 * One invoice with its `fiscal_messages` audit trail.
 *
 * `include_messages=true` is explicit rather than implied: the XML both ways
 * for every attempt is by far the largest thing this API returns, and the list
 * screen has no use for it. The detail screen always wants it — that is what
 * the screen is for — so it is not exposed as an option here.
 */
export function useInvoice(id: string): UseQueryResult<InvoiceDetail, unknown> {
  return useQuery({
    queryKey: keys.invoice(id),
    queryFn: () =>
      api.get<InvoiceDetail>(`/invoices/${id}`, { query: { include_messages: true } }),
    enabled: Boolean(id),
  });
}

/**
 * Manual retry of a stuck invoice.
 *
 * `POST /v1/invoices/:id/retry` answers with the **attempt** shape
 * (`InvoiceResponse` in `openapi/fiskhub.v1.yaml`) — the ten fields a till
 * needs to finish printing a receipt. It is not the detail shape: no
 * `messages`, no `deadline_at`, no `attempts`.
 *
 * So the result is deliberately NOT written into the detail cache. Doing that
 * replaced the open invoice with the smaller object and the detail screen threw
 * on `invoice.messages` the moment it re-rendered — clicking Retry took the page
 * down. Invalidating instead refetches the real detail, which is also the only
 * way to pick up the `fiscal_messages` row the retry just wrote.
 */
export function useRetryInvoice(): UseMutationResult<InvoiceResponse, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InvoiceResponse>(`/invoices/${id}/retry`),
    onSuccess: (attempt) => {
      void queryClient.invalidateQueries({ queryKey: keys.invoice(attempt.id) });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

/** Streams the CSV straight from the API so the export matches the filters. */
export async function exportInvoicesCsv(filters: InvoiceFilters): Promise<Blob> {
  const response = await request<Response>("/invoices/export", {
    method: "GET",
    query: { ...filters, limit: undefined, cursor: undefined },
    raw: true,
  });
  return response.blob();
}

// -----------------------------------------------------------------------------
// Users and audit
// -----------------------------------------------------------------------------

export function useUsers(): UseQueryResult<User[], unknown> {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => fetchAll<User>("/users"),
  });
}

/**
 * Invite by email. No password crosses this boundary: the API creates the row
 * with a one-time link and the invitee chooses their own on the accept screen.
 * The response says whether the email actually went out.
 */
export function useInviteUser(): UseMutationResult<
  InvitedUser,
  unknown,
  { email: string; role: User["role"] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: User["role"] }) =>
      api.post<InvitedUser>("/users", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
    },
  });
}

/** A fresh link for a user who has not yet accepted. The old one stops working. */
export function useResendInvitation(): UseMutationResult<InvitedUser, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InvitedUser>(`/users/${id}/invitation`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
    },
  });
}

// -----------------------------------------------------------------------------
// Accepting an invitation — unauthenticated by nature
// -----------------------------------------------------------------------------

/**
 * What the link in the email is for. A POST rather than a GET so the token
 * travels in the body, where the API's request log never looks.
 */
export function useInvitationPreview(
  token: string | undefined,
): UseQueryResult<InvitationPreview, unknown> {
  return useQuery({
    queryKey: ["invitation", token ?? ""] as const,
    queryFn: () => api.post<InvitationPreview>("/auth/invitations/inspect", { token }),
    enabled: token !== undefined && token.length > 0,
    // Every failure here is a fact about the link — not valid, expired — and
    // asking again will not change it.
    retry: false,
    staleTime: Infinity,
    // Opted out of the app-wide `refetchOnMount: "always"`, which exists so a
    // screen someone navigated to shows current data. This is not that: the
    // token is inspected once and the answer cannot change between mounts.
    refetchOnMount: false,
  });
}

/** Choose a password and sign in. The API answers with the session, like login. */
export function useAcceptInvitation(): UseMutationResult<
  Session,
  unknown,
  { token: string; password: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<Session>("/auth/invitations/accept", input),
    onSuccess: (session) => {
      queryClient.setQueryData(keys.session, session);
      void queryClient.invalidateQueries();
    },
  });
}

export function useUpdateUser(): UseMutationResult<
  User,
  unknown,
  { id: string; patch: { role?: User["role"]; status?: User["status"] } }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { role?: User["role"]; status?: User["status"] };
    }) => api.patch<User>(`/users/${id}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
    },
  });
}

export interface AuditFilters {
  action?: string;
  actor?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

/**
 * One page of the audit log.
 *
 * The path is `/audit-log`, not `/audit` — that is where `routes/index.ts`
 * mounts it.
 */
export function useAuditLog(filters: AuditFilters): UseQueryResult<ListPage<AuditEntry>, unknown> {
  return useQuery({
    queryKey: keys.audit(filters),
    queryFn: () => api.get<ListPage<AuditEntry>>("/audit-log", { query: { ...filters } }),
    placeholderData: (previous) => previous,
  });
}
